<template>
  <v-container v-if="loadError" class="prompt-detail-page" fluid>
    <v-alert type="error" variant="tonal" class="mt-6">
      <div class="d-flex align-center">
        <div class="flex-grow-1">
          <strong>Proposal #{{ $route.params.id }} not found.</strong>
          <div class="text-body-2 mt-1">{{ loadError }}</div>
        </div>
        <v-btn :to="{ name: 'prompt-tuning-queue' }" variant="outlined" class="ms-4"> Back to queue </v-btn>
      </div>
    </v-alert>
  </v-container>
  <v-container v-else-if="!proposal" class="prompt-detail-page" fluid>
    <v-skeleton-loader type="card, list-item-three-line, list-item-three-line" />
  </v-container>
  <v-container v-else class="prompt-detail-page" fluid>
    <header class="page-header">
      <div class="page-header__left">
        <v-btn icon="mdi-arrow-left" variant="text" :to="{ name: 'prompt-tuning-queue' }" />
        <div>
          <h1 class="page-title">Proposal #{{ proposal.id }}</h1>
          <div class="page-meta">
            <span class="status-chip" :class="`status-chip--${proposal.status.toLowerCase()}`">
              {{ proposal.status }}
            </span>
            <span class="meta-item">{{ proposal.clientCompanyName }}</span>
            <span class="meta-item">{{ configKeyLabel(proposal.configKey) }}</span>
            <span class="meta-item">{{ proposal.attempts }} attempt{{ proposal.attempts === 1 ? "" : "s" }}</span>
            <span class="meta-item">
              Gate
              <v-icon v-if="proposal.gatePassed" size="small" color="success">mdi-check-circle</v-icon>
              <v-icon v-else size="small" color="error">mdi-close-circle-outline</v-icon>
              <span v-if="effectiveGate.waived" class="meta-item">({{ effectiveGate.waived }} waived)</span>
            </span>
          </div>
        </div>
      </div>
    </header>

    <!-- Agent in progress -->
    <section v-if="agentWorking" class="card card--progress">
      <v-progress-linear indeterminate color="primary" rounded height="6" class="mb-3" />
      <p class="progress-text">
        The agent is drafting and validating a candidate prompt — attempt {{ (proposal.runs?.length || 0) + 1 }}. Each
        attempt re-runs every feedback row through the model, so this can take a few minutes. This page refreshes
        automatically.
      </p>
    </section>

    <!-- Gate override record -->
    <v-alert v-if="proposal.gateOverriddenOn" type="warning" variant="tonal" class="mb-5">
      Validation gate was overridden by {{ proposal.gateOverriddenByEmail || "an admin" }} on
      {{ new Date(proposal.gateOverriddenOn).toLocaleString() }}: {{ proposal.gateOverrideReason }}
    </v-alert>

    <!-- Patterns -->
    <section v-if="proposal.patternSummary?.length" class="card">
      <h2 class="card-title">Patterns the agent identified</h2>
      <article v-for="(p, idx) in proposal.patternSummary" :key="idx" class="pattern">
        <header class="pattern__header">
          <span class="pattern__name">{{ p.pattern }}</span>
          <span class="pattern__count"
            >{{ (p.feedbackIds || []).length }} feedback row{{ (p.feedbackIds || []).length === 1 ? "" : "s" }}</span
          >
        </header>
        <p class="pattern__root">{{ p.rootCause }}</p>
      </article>
    </section>

    <!-- Conflicts -->
    <section v-if="proposal.conflicts?.length" class="card card--warn">
      <h2 class="card-title">
        <v-icon color="warning">mdi-alert-circle-outline</v-icon>
        Conflicts requiring your decision
      </h2>
      <article v-for="(c, idx) in proposal.conflicts" :key="idx" class="conflict">
        <p class="conflict__issue">{{ c.issue }}</p>
        <v-radio-group :model-value="c.resolvedOptionIndex" @update:model-value="resolveConflict(idx, $event)">
          <v-radio v-for="(opt, optIdx) in c.options" :key="optIdx" :label="opt" :value="optIdx">
            <template #label>
              <span>
                {{ opt }}
                <em v-if="optIdx === c.recommendedOption" class="recommended">(recommended)</em>
              </span>
            </template>
          </v-radio>
        </v-radio-group>
      </article>
    </section>

    <!-- Changes summary -->
    <section v-if="proposal.changesSummary?.length" class="card">
      <h2 class="card-title">Changes the agent made</h2>
      <v-expansion-panels variant="accordion" multiple>
        <v-expansion-panel v-for="(change, idx) in proposal.changesSummary" :key="idx">
          <v-expansion-panel-title>
            <div class="change-title">
              <code class="mono">{{ change.section }}</code>
              <span class="change-description">{{ change.description }}</span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div class="snippet-grid">
              <div v-if="change.beforeSnippet" class="snippet snippet--before">
                <div class="snippet__label">Before</div>
                <pre>{{ change.beforeSnippet }}</pre>
              </div>
              <div v-if="change.afterSnippet" class="snippet snippet--after">
                <div class="snippet__label">After</div>
                <pre>{{ change.afterSnippet }}</pre>
              </div>
            </div>
            <p v-if="(change.triggeringFeedbackIds || []).length" class="triggers">
              Driven by feedback row{{ change.triggeringFeedbackIds.length === 1 ? "" : "s" }}:
              <code v-for="fid in change.triggeringFeedbackIds" :key="fid" class="mono">#{{ fid }}</code>
            </p>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>

    <!-- Prompt editor -->
    <section class="card">
      <header class="card-header-row">
        <h2 class="card-title">Prompt</h2>
        <div class="card-actions">
          <v-btn
            variant="outlined"
            :prepend-icon="editing ? 'mdi-close' : 'mdi-pencil'"
            :disabled="agentWorking || revalidating"
            @click="toggleEdit">
            {{ editing ? "Cancel" : "Edit" }}
          </v-btn>
          <v-btn
            variant="flat"
            color="primary"
            prepend-icon="mdi-play"
            :loading="revalidating"
            :disabled="agentWorking"
            @click="revalidate">
            Re-validate
          </v-btn>
        </div>
      </header>

      <div v-if="!editing" class="prompt-view">
        <pre class="prompt-view__text">{{ proposal.proposedPrompt }}</pre>
      </div>
      <v-textarea
        v-else
        v-model="editingPrompt"
        rows="20"
        auto-grow
        variant="outlined"
        class="prompt-editor"
        hide-details
        @keydown.tab.prevent="insertTab" />
      <v-textarea
        v-if="editing"
        v-model="editingNotes"
        label="FDE notes"
        rows="2"
        auto-grow
        variant="outlined"
        class="mt-3"
        hide-details />
      <div v-if="editing" class="edit-actions">
        <v-btn color="primary" variant="flat" :loading="savingEdit" @click="saveEdit"> Save edits </v-btn>
        <span class="edit-hint"> After saving you must re-validate before approving. </span>
      </div>
    </section>

    <!-- Validation panel -->
    <section v-if="latestRun" class="card">
      <h2 class="card-title">Latest validation</h2>
      <div v-if="revalidating" class="validation-progress">
        <v-progress-linear indeterminate color="primary" rounded height="6" />
        <span class="progress-text">
          Re-running {{ proposal.feedbackIds?.length || 0 }} feedback rows through the model — this can take a minute or
          two.
        </span>
      </div>
      <div class="validation-summary">
        <span class="meta-item">
          <strong>Gate:</strong>
          <v-icon v-if="effectiveGate.passed" color="success">mdi-check-circle</v-icon>
          <v-icon v-else color="error">mdi-close-circle-outline</v-icon>
        </span>
        <span class="meta-item">
          <strong>Feedback rerun:</strong>
          {{ effectiveGate.matched }}/{{ effectiveGate.usable }} matched<template v-if="effectiveGate.waived">
            · {{ effectiveGate.waived }} waived</template
          >
        </span>
        <span class="meta-item">
          <strong>Fixture rerun:</strong>
          {{ latestRun.fixtureRerun?.skipped || `${(latestRun.fixtureRerun?.regressed || []).length} regressed` }}
        </span>
      </div>
      <details class="rerun-details" open>
        <summary>Per-row results</summary>
        <div class="rerun-table-scroll">
          <table class="rerun-table">
            <thead>
              <tr>
                <th>Feedback</th>
                <th>Field</th>
                <th>Expected</th>
                <th>Got</th>
                <th>Status</th>
                <th>Via</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in latestRun.feedbackRerun?.perRow || []" :key="row.id" :class="rowClass(row)">
                <td>#{{ row.id }}</td>
                <td>
                  <code class="mono">{{ row.field }}</code>
                </td>
                <td>{{ row.expected }}</td>
                <td>{{ row.got || "—" }}</td>
                <td>
                  <v-tooltip v-if="waivedInfo(row.id)" location="top">
                    <template #activator="{ props: tp }">
                      <span v-bind="tp">⊘ waived</span>
                    </template>
                    {{ waivedInfo(row.id).byEmail }}: {{ waivedInfo(row.id).reason }}
                  </v-tooltip>
                  <span v-else-if="row.matched">✓ match</span>
                  <span v-else-if="row.status === 'skipped'">⤳ {{ row.reason }}</span>
                  <span v-else>✗ miss</span>
                </td>
                <td>
                  <v-tooltip v-if="row.matched && row.matchedVia === 'llm_judge'" location="top">
                    <template #activator="{ props: tp }">
                      <span v-bind="tp" class="via-chip via-chip--judge">AI judge</span>
                    </template>
                    {{ row.judgeReason || "Judged equivalent" }}
                  </v-tooltip>
                  <span v-else-if="row.matched" class="via-chip">{{ matchedViaLabel(row.matchedVia) }}</span>
                </td>
                <td class="row-actions">
                  <v-btn
                    v-if="waivedInfo(row.id) && proposalOpen"
                    size="x-small"
                    variant="text"
                    :disabled="revalidating || savingWaive"
                    @click="unwaive(row.id)">
                    Unwaive
                  </v-btn>
                  <v-btn
                    v-else-if="!row.matched && row.status === 'ok' && proposalOpen"
                    size="x-small"
                    variant="outlined"
                    :disabled="revalidating || savingWaive"
                    @click="openWaive(row.id)">
                    Waive
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </section>

    <!-- Footer actions -->
    <footer class="action-footer">
      <v-btn
        size="large"
        color="success"
        variant="flat"
        :disabled="!canApprove"
        :loading="approving"
        prepend-icon="mdi-check"
        @click="approve">
        Approve &amp; apply
      </v-btn>
      <v-btn
        v-if="canOverrideApprove"
        size="large"
        variant="outlined"
        color="warning"
        prepend-icon="mdi-shield-alert-outline"
        :loading="approving"
        @click="overrideDialog = true">
        Override gate &amp; approve
      </v-btn>
      <v-btn
        size="large"
        variant="outlined"
        color="error"
        prepend-icon="mdi-close"
        :disabled="proposal.status === 'APPLIED' || proposal.status === 'REJECTED'"
        @click="rejectDialog = true">
        Reject
      </v-btn>
      <span v-if="!canApprove && !approveDisabledReason.empty" class="approve-disabled-reason">
        {{ approveDisabledReason.text }}
      </span>
    </footer>

    <v-dialog v-model="waiveDialog.open" max-width="480">
      <v-card>
        <v-card-title>Waive feedback row #{{ waiveDialog.feedbackId }}</v-card-title>
        <v-card-text>
          <p class="dialog-hint">
            The row is excluded from the validation gate — use this when the feedback itself was wrong or the mismatch
            is acceptable. Recorded with your name and reason.
          </p>
          <v-textarea
            v-model="waiveDialog.reason"
            label="Reason (required)"
            variant="outlined"
            rows="2"
            auto-grow
            hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="waiveDialog.open = false">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="savingWaive"
            :disabled="!waiveDialog.reason.trim()"
            @click="submitWaive">
            Waive row
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="overrideDialog" max-width="520">
      <v-card>
        <v-card-title>Override validation gate</v-card-title>
        <v-card-text>
          <p class="dialog-hint">
            The gate has not passed ({{ effectiveGate.matched }}/{{ effectiveGate.usable }} matched). Approving anyway
            applies the prompt to production for this client. Your name and reason are recorded on the proposal.
          </p>
          <v-textarea
            v-model="overrideReason"
            label="Reason (required)"
            variant="outlined"
            rows="2"
            auto-grow
            hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="overrideDialog = false">Cancel</v-btn>
          <v-btn
            color="warning"
            variant="flat"
            :loading="approving"
            :disabled="!overrideReason.trim()"
            @click="approveWithOverride">
            Override &amp; apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="480">
      <v-card>
        <v-card-title>Reject proposal</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="rejectNotes"
            label="Reason for rejection (optional)"
            variant="outlined"
            rows="3"
            auto-grow
            hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="rejectDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="rejecting" @click="reject">Reject</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
/* eslint-disable no-console -- error surfacing is via console for v1; UI snackbar TBD */
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { promptTuningProposals } from "@/services/promptTuningService";
import { configKeyLabel } from "@/services/promptTuningConstants";

const route = useRoute();

const proposal = ref(null);
const loadError = ref(null);
const editing = ref(false);
const editingPrompt = ref("");
const editingNotes = ref("");

const savingEdit = ref(false);
const revalidating = ref(false);
const approving = ref(false);
const rejecting = ref(false);
const savingWaive = ref(false);

const rejectDialog = ref(false);
const rejectNotes = ref("");
const waiveDialog = ref({ open: false, feedbackId: null, reason: "" });
const overrideDialog = ref(false);
const overrideReason = ref("");

const MATCHED_VIA_LABELS = {
  option_ids: "option IDs",
  label: "label",
  exact: "exact",
  llm_judge: "AI judge",
};

const latestRun = computed(() => {
  const runs = proposal.value?.runs;
  return runs && runs.length ? runs[runs.length - 1] : null;
});

const effectiveGate = computed(
  () => proposal.value?.effectiveGate || { passed: false, matched: 0, usable: 0, waived: 0, fixturesRegressed: 0 }
);

const proposalOpen = computed(() => proposal.value && !["APPLIED", "REJECTED"].includes(proposal.value.status));

const agentWorking = computed(() => proposal.value?.status === "PENDING" && !proposal.value?.proposedPrompt);

const promptInSyncWithLastRun = computed(() => {
  if (!latestRun.value) return false;
  return latestRun.value.candidatePrompt === proposal.value?.proposedPrompt;
});

const canApprove = computed(() => {
  if (!proposalOpen.value) return false;
  return effectiveGate.value.passed && promptInSyncWithLastRun.value;
});

const canOverrideApprove = computed(() => {
  if (!proposalOpen.value || canApprove.value) return false;
  return !effectiveGate.value.passed && promptInSyncWithLastRun.value && !agentWorking.value;
});

const approveDisabledReason = computed(() => {
  if (canApprove.value || !proposal.value) return { empty: true, text: "" };
  if (["APPLIED", "REJECTED"].includes(proposal.value.status)) {
    return { empty: false, text: `Proposal is ${proposal.value.status}.` };
  }
  if (agentWorking.value) {
    return { empty: false, text: "The agent is still working on a candidate prompt." };
  }
  if (effectiveGate.value.fixturesRegressed) {
    return {
      empty: false,
      text: `Validation gate has not passed — ${effectiveGate.value.fixturesRegressed} fixture regression(s); waiving rows will not clear this.`,
    };
  }
  if (!effectiveGate.value.passed) {
    return {
      empty: false,
      text: "Validation gate has not passed — waive the failing rows, or use the gate override.",
    };
  }
  if (!promptInSyncWithLastRun.value) {
    return { empty: false, text: "Prompt was edited — re-validate before approving." };
  }
  return { empty: true, text: "" };
});

function waivedInfo(feedbackId) {
  const override = (proposal.value?.rowOverrides || {})[String(feedbackId)];
  return override?.action === "waived" ? override : null;
}

function matchedViaLabel(via) {
  return MATCHED_VIA_LABELS[via] || via || "";
}

function rowClass(row) {
  if (waivedInfo(row.id)) return "row-waived";
  return row.matched ? "row-pass" : "row-fail";
}

let pollTimer = null;

function schedulePoll() {
  clearTimeout(pollTimer);
  if (!agentWorking.value) return;
  pollTimer = setTimeout(async () => {
    await load();
    schedulePoll();
  }, 5000);
}

onBeforeUnmount(() => clearTimeout(pollTimer));

async function load() {
  loadError.value = null;
  try {
    proposal.value = await promptTuningProposals.detail(route.params.id);
  } catch (err) {
    proposal.value = null;
    const status = err?.response?.status;
    loadError.value =
      status === 404
        ? `No proposal with id ${JSON.stringify(route.params.id)} exists.`
        : err?.message || "Failed to load proposal.";
    console.error("Proposal load failed", err);
  }
}

function toggleEdit() {
  if (!editing.value) {
    editingPrompt.value = proposal.value?.proposedPrompt || "";
    editingNotes.value = proposal.value?.fdeNotes || "";
  }
  editing.value = !editing.value;
}

function insertTab(event) {
  const target = event.target;
  const start = target.selectionStart;
  const end = target.selectionEnd;
  editingPrompt.value = editingPrompt.value.slice(0, start) + "  " + editingPrompt.value.slice(end);
  // Move caret after the insert.
  requestAnimationFrame(() => {
    target.selectionStart = target.selectionEnd = start + 2;
  });
}

async function saveEdit() {
  savingEdit.value = true;
  try {
    proposal.value = await promptTuningProposals.edit(proposal.value.id, {
      proposed_prompt: editingPrompt.value,
      fde_notes: editingNotes.value,
    });
    editing.value = false;
  } catch (err) {
    console.error("Save failed", err);
  } finally {
    savingEdit.value = false;
  }
}

async function revalidate() {
  revalidating.value = true;
  try {
    proposal.value = await promptTuningProposals.revalidate(proposal.value.id);
  } catch (err) {
    console.error("Revalidate failed", err);
  } finally {
    revalidating.value = false;
  }
}

async function resolveConflict(idx, chosen) {
  if (chosen == null) return;
  try {
    proposal.value = await promptTuningProposals.resolveConflict(proposal.value.id, idx, {
      chosen_option_index: chosen,
    });
  } catch (err) {
    console.error("Conflict resolution failed", err);
  }
}

async function approve() {
  approving.value = true;
  try {
    proposal.value = await promptTuningProposals.approve(proposal.value.id);
  } catch (err) {
    console.error("Approve failed", err);
  } finally {
    approving.value = false;
  }
}

async function approveWithOverride() {
  approving.value = true;
  try {
    proposal.value = await promptTuningProposals.approve(proposal.value.id, {
      override_gate: true,
      reason: overrideReason.value,
    });
    overrideDialog.value = false;
    overrideReason.value = "";
  } catch (err) {
    console.error("Override approve failed", err);
  } finally {
    approving.value = false;
  }
}

function openWaive(feedbackId) {
  waiveDialog.value = { open: true, feedbackId, reason: "" };
}

async function submitWaive() {
  savingWaive.value = true;
  try {
    proposal.value = await promptTuningProposals.waiveRow(proposal.value.id, waiveDialog.value.feedbackId, {
      waived: true,
      reason: waiveDialog.value.reason,
    });
    waiveDialog.value = { open: false, feedbackId: null, reason: "" };
  } catch (err) {
    console.error("Waive failed", err);
  } finally {
    savingWaive.value = false;
  }
}

async function unwaive(feedbackId) {
  savingWaive.value = true;
  try {
    proposal.value = await promptTuningProposals.waiveRow(proposal.value.id, feedbackId, { waived: false });
  } catch (err) {
    console.error("Unwaive failed", err);
  } finally {
    savingWaive.value = false;
  }
}

async function reject() {
  rejecting.value = true;
  try {
    proposal.value = await promptTuningProposals.reject(proposal.value.id, {
      notes: rejectNotes.value,
    });
    rejectDialog.value = false;
  } catch (err) {
    console.error("Reject failed", err);
  } finally {
    rejecting.value = false;
  }
}

onMounted(async () => {
  await load();
  schedulePoll();
});
</script>

<style lang="scss" scoped>
.prompt-detail-page {
  padding: 24px 32px 96px;
  max-width: 1400px;

  @media (max-width: 600px) {
    padding: 16px 16px 96px;
  }
}

.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.page-title {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.page-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.status-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;

  &--pending {
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.7);
  }
  &--low_confidence {
    background: rgba(255, 165, 0, 0.15);
    color: rgb(180, 110, 0);
  }
  &--approved {
    background: rgba(34, 139, 230, 0.12);
    color: rgb(20, 110, 200);
  }
  &--applied {
    background: rgba(46, 160, 67, 0.15);
    color: rgb(30, 130, 60);
  }
  &--rejected {
    background: rgba(212, 70, 70, 0.12);
    color: rgb(180, 50, 50);
  }
}

.card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;

  &--warn {
    border-color: rgba(255, 165, 0, 0.4);
    background: rgba(255, 165, 0, 0.04);
  }
}

.card-title {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.card-actions {
  display: flex;
  gap: 8px;
}

.pattern {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 12px 0;

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 4px;
  }

  &__name {
    font-family: "DM Sans", system-ui, sans-serif;
    font-weight: 600;
    font-size: 14px;
  }

  &__count {
    font-family: "JetBrains Mono", ui-monospace, monospace;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
  }

  &__root {
    margin: 0;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.7);
  }
}

.conflict {
  border-top: 1px solid rgba(255, 165, 0, 0.2);
  padding-top: 12px;
  margin-top: 12px;

  &:first-of-type {
    border-top: none;
    padding-top: 0;
    margin-top: 0;
  }

  &__issue {
    font-family: "DM Sans", system-ui, sans-serif;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }
}

.recommended {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  margin-left: 6px;
}

/* Expansion panel container — adds a single outer border so the group reads
   as one component. Background on the container (not just the title)
   eliminates the white sliver at the rounded corners where the title's
   square background and the container's rounded clip mismatch. */
.card :deep(.v-expansion-panels) {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: none;
  background: rgba(0, 0, 0, 0.025);
}

/* Vuetify renders extra wrappers (`v-expansion-panel`, its
   `__shadow` pseudo, etc.) — flatten all of them so no white seeps
   through at the rounded outer corners. */
.card :deep(.v-expansion-panel),
.card :deep(.v-expansion-panel::after),
.card :deep(.v-expansion-panel::before) {
  background: transparent !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.card :deep(.v-expansion-panel__shadow) {
  display: none !important;
}

.card :deep(.v-expansion-panel-title) {
  background: rgba(0, 0, 0, 0.025);
  min-height: 56px;
  transition: background 120ms ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.87);
}

.card :deep(.v-expansion-panel:last-child .v-expansion-panel-title) {
  border-bottom: none;
}

.card :deep(.v-expansion-panel-title:hover) {
  background: rgba(0, 0, 0, 0.05);
}

.card :deep(.v-expansion-panel--active > .v-expansion-panel-title) {
  background: rgba(34, 139, 230, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.card :deep(.v-expansion-panel-title__icon) {
  color: rgba(0, 0, 0, 0.55);
}

.card :deep(.v-expansion-panel-text__wrapper) {
  background: #ffffff;
  padding-top: 16px;
}

.change-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.change-title code {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.78);
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.change-description {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.78);
}

.snippet-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.snippet {
  border-radius: 6px;
  padding: 10px 12px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  overflow-x: auto;

  &__label {
    font-family: "DM Sans", system-ui, sans-serif;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  &--before {
    background: rgba(212, 70, 70, 0.07);
    color: rgb(120, 30, 30);
  }

  &--after {
    background: rgba(46, 160, 67, 0.07);
    color: rgb(20, 90, 40);
  }

  pre {
    margin: 0;
    white-space: pre-wrap;
    font-family: inherit;
  }
}

.triggers {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;

  code {
    background: rgba(0, 0, 0, 0.05);
    padding: 1px 4px;
    border-radius: 3px;
    margin-left: 4px;
  }
}

.prompt-view__text {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px;
  line-height: 1.6;
  background: rgba(0, 0, 0, 0.025);
  border-radius: 6px;
  padding: 16px;
  margin: 0;
  white-space: pre-wrap;
  max-height: 480px;
  overflow-y: auto;
}

.prompt-editor :deep(textarea) {
  font-family: "JetBrains Mono", ui-monospace, monospace !important;
  font-size: 12px;
  line-height: 1.6;
}

.edit-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
}

.edit-hint {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
}

.validation-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 14px;
}

.rerun-details {
  margin-top: 8px;

  summary {
    cursor: pointer;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.7);
    margin-bottom: 8px;
  }
}

.rerun-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.rerun-table {
  width: 100%;
  min-width: 480px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  .row-pass td:nth-child(5) {
    color: rgb(30, 130, 60);
  }
  .row-fail td:nth-child(5) {
    color: rgb(180, 50, 50);
  }
  .row-waived td {
    color: rgba(0, 0, 0, 0.4);
  }
}

.row-actions {
  white-space: nowrap;
}

.via-chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.65);
  font-size: 11px;

  &--judge {
    background: rgba(140, 80, 200, 0.12);
    color: rgb(110, 60, 170);
    cursor: help;
  }
}

.card--progress {
  border-color: rgba(34, 139, 230, 0.35);
  background: rgba(34, 139, 230, 0.04);
}

.progress-text {
  font-family: "DM Sans", system-ui, sans-serif;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  margin: 0;
}

.validation-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.dialog-hint {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 12px;
}

.action-footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.approve-disabled-reason {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
}

.mono {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px;
}
</style>
